import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { makeApiCall } from "@swiggy-private/js-utils";
import {
    useDigitalMenuConfig,
    ConfigLoadingStatus,
    SwiggyContextHeader,
    hasMinimumPlVersion,
} from "@swiggy-private/portal-dineout-context";
import type { IDigitalMenuConfigData } from "@swiggy-private/portal-dineout-context";
import { fetchDigitalMenuConfig } from "api/config";
import camelcaseKeys from "camelcase-keys";
import { IDigitalMenuConfigResponse } from "types/config";
import { sendCustomNREvViaBlaze } from "utils/newrelic";
import { NATIVE_BRIDGE_TIMEOUT_MS, NR_TABLES, SUCCESS_STATUS_CODE } from "../constants/api";
import { getBridgeSdk } from "../utils/bridgeSdk";
import { isApp, isBrowserEnv } from "../utils/browser";
import { getForceFetchConfigFromStorage } from "../utils/sessionFlags";

interface IUseFetchDigitalMenuConfigParams {
    restaurantId: string;
    isRouterReady: boolean;
}

interface INativeBridgeConfigResponse {
    isAvailable: boolean;
    data?: {
        restaurant_id: string;
        version: string;
        menu_layout: string;
        theme_info: {
            theme: string;
        };
    };
}

const fetchConfigFromNativeBridge = async (restaurantId: string): Promise<INativeBridgeConfigResponse | undefined> => {
    return getBridgeSdk().getDigitalMenuConfig({ restaurantId }, NATIVE_BRIDGE_TIMEOUT_MS);
};

export const useFetchDigitalMenuConfig = ({ restaurantId, isRouterReady }: IUseFetchDigitalMenuConfigParams) => {
    const { setConfig, setLoadingStatus, setError, loadingStatus, config, isConfigLoaded } = useDigitalMenuConfig();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const hasFetchedRef = useRef(false);
    const [isFetching, setIsFetching] = useState(false);

    // Sync hasFetchedRef with isConfigLoaded to prevent refetch on remount
    useEffect(() => {
        if (isConfigLoaded) {
            hasFetchedRef.current = true;
        }
    }, [isConfigLoaded]);

    const setThemeAndConfig = useCallback(
        (configData: IDigitalMenuConfigData) => {
            // Only update config - theme will be updated by ThemeContext watching config changes
            setConfig(configData);
            setLoadingStatus(ConfigLoadingStatus.SUCCESS);
        },
        [setConfig, setLoadingStatus],
    );

    const fetchDMConfig = useCallback(async () => {
        const response = await fetchDigitalMenuConfig({ restaurantId }, makeApiCall);
        return response;
    }, [restaurantId]);

    const updateConversionNudgesConfig = useCallback(
        async (appConfig: IDigitalMenuConfigData) => {
            try {
                const response = await fetchDMConfig();
                if (response?.statusCode === SUCCESS_STATUS_CODE) {
                    const configData = (response as unknown as IDigitalMenuConfigResponse)?.config;
                    if (configData?.conversionNudgesConfig) {
                        setThemeAndConfig({ ...appConfig, conversionNudgesConfig: configData.conversionNudgesConfig });
                    }
                }
            } catch (_err) {
                sendCustomNREvViaBlaze(NR_TABLES.CONFIG_FETCH_DMV2_API_ERROR, {});
            }
        },
        [fetchDMConfig, setThemeAndConfig],
    );

    const fetchConfigFromApi = useCallback(async () => {
        if (!restaurantId) {
            return;
        }

        try {
            const response = await fetchDMConfig();
            const configData = (response as unknown as IDigitalMenuConfigResponse)?.config;

            if (response.statusCode === SUCCESS_STATUS_CODE) {
                setThemeAndConfig(configData as unknown as IDigitalMenuConfigData);
            } else {
                setError("Failed to fetch config from API");
                setLoadingStatus(ConfigLoadingStatus.ERROR);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : "Unknown error fetching config");
            setLoadingStatus(ConfigLoadingStatus.ERROR);
        }
    }, [restaurantId, fetchDMConfig, setThemeAndConfig, setError, setLoadingStatus]);

    const shouldForceFetchConfig = getForceFetchConfigFromStorage();

    const fetchConfig = useCallback(async () => {
        // Guard: prevent duplicate calls and ensure all dependencies are ready
        // Also check if config already loaded to prevent refetch on remount
        if (hasFetchedRef.current || !restaurantId || isConfigLoaded) {
            return;
        }

        hasFetchedRef.current = true;
        setIsFetching(true);
        setLoadingStatus(ConfigLoadingStatus.LOADING);

        const isNativeApp = isApp();

        /** TODO: check during regression if app version check is needed */
        if (isNativeApp && hasMinimumPlVersion(swiggyHeaders) && !shouldForceFetchConfig) {
            // For native apps, try native bridge first with built-in timeout
            try {
                const nativeBridgeResponse = await fetchConfigFromNativeBridge(restaurantId);

                if (nativeBridgeResponse?.data) {
                    // Convert snake_case response from native bridge to camelCase because apps get it in snake_case
                    const configResponse = camelcaseKeys(nativeBridgeResponse.data, {
                        deep: true,
                    }) as unknown as IDigitalMenuConfigData;

                    setThemeAndConfig(configResponse);
                    setIsFetching(false);
                    updateConversionNudgesConfig(configResponse);

                    return;
                }

                // Native bridge returned but config not available, fallback to API
                await fetchConfigFromApi();
            } catch {
                // Native bridge failed or timed out, fallback to API
                await fetchConfigFromApi();
            }
        } else {
            // For web platform, directly call the API
            await fetchConfigFromApi();
        }

        setIsFetching(false);
    }, [
        restaurantId,
        isConfigLoaded,
        setLoadingStatus,
        swiggyHeaders,
        shouldForceFetchConfig,
        fetchConfigFromApi,
        setThemeAndConfig,
        updateConversionNudgesConfig,
    ]);

    // Retry function to refetch config after an error
    const retryFetchConfig = useCallback(() => {
        hasFetchedRef.current = false;
        setError(null);
        fetchConfig();
    }, [fetchConfig, setError]);

    useEffect(() => {
        // Only fetch when all dependencies are ready:
        const canFetch = isBrowserEnv() && isRouterReady && restaurantId;
        // Don't fetch if config already loaded (prevents duplicate calls on remount)
        const shouldFetch = canFetch && !hasFetchedRef.current && !isFetching && !isConfigLoaded;

        if (shouldFetch) {
            fetchConfig();
        }
    }, [isRouterReady, restaurantId, isFetching, fetchConfig, isConfigLoaded]);

    return {
        config,
        loadingStatus,
        isConfigLoaded,
        isFetching,
        hasFetched: hasFetchedRef.current && !isFetching,
        hasError: loadingStatus === ConfigLoadingStatus.ERROR,
        retryFetchConfig,
    };
};
